// Privacy policy v1.0 (effective 1 September 2026), served until v1.1 takes effect on 1 November 2026
// (src/content/legal/index.ts getGdprDoc). Delete this folder after that date.
import type { LegalDoc } from "../types";

// Malay (ms). Terjemahan dasar rujukan (v1.0, berkuat kuasa 1 September 2026).
const gdprMs: LegalDoc = {
  title: "Dasar Privasi & Perlindungan Data (GDPR / RGPD)",
  effectiveDate: "1 September 2026",
  version: "1.0",
  intro:
    "Dasar ini menerangkan cara Photohawk mengendalikan data peribadi di bawah Peraturan Perlindungan Data Am EU (GDPR) dan padanan Perancisnya, RGPD (Règlement Général sur la Protection des Données), serta UK GDPR. RGPD dan GDPR merujuk undang-undang yang sama.",
  sections: [
    {
      heading: "1. Siapa kami",
      blocks: [
        {
          type: "p",
          text: "Photohawk ialah platform fotografi acara yang membolehkan jurugambar dan penganjur menerbitkan, mencari dan menjual foto acara.",
        },
        {
          type: "ul",
          items: [
            "Entiti undang-undang: CLOUDCONCUR LTD (nombor syarikat 13399913), sebuah syarikat yang didaftarkan di England & Wales, beroperasi dengan nama dagangan Photohawk.",
            "Alamat berdaftar: 23 Caldey Gardens, Ingleby Barwick, Stockton-on-Tees, TS17 5HW, United Kingdom.",
            "Pegawai Perlindungan Data (DPO): Andrew Hammond — hubungi support@photohawk.com.",
            "Wakil EU (GDPR Art. 27): kami tidak melantik wakil EU, atas dasar bahawa pemprosesan data penduduk EU oleh kami adalah sekali-sekala dan bukan aktiviti utama kami (Art. 27(2)). Kami sentiasa mengkaji semula perkara ini dan akan melantik seorang wakil jika pemprosesan kami berubah.",
            "Pihak berkuasa penyeliaan: Information Commissioner's Office (ICO) di UK, dan pihak berkuasa EU termasuk CNIL Perancis bagi individu di EU.",
          ],
        },
      ],
    },
    {
      heading: "2. Dua peranan kami — pengawal dan pemproses",
      blocks: [
        {
          type: "p",
          text: "Photohawk berurusan dengan dua jenis orang, dan peranan undang-undang kami berbeza bagi setiap satu:",
        },
        {
          type: "ul",
          items: [
            "Jurugambar dan penganjur (pelanggan kami). Bagi data akaun anda (nama, e-mel, butiran perniagaan, log masuk, maklumat pengebilan dan pembayaran keluar), Photohawk ialah pengawal data.",
            "Peserta acara dan pembeli (pelanggan anda). Bagi data orang yang terdapat dalam foto dan yang membelinya, anda (jurugambar/penganjur) ialah pengawal data dan Photohawk ialah pemproses data anda — kami memproses data tersebut mengikut arahan anda untuk menjalankan galeri, carian dan jualan yang telah anda sediakan. Suatu Perjanjian Pemprosesan Data (lihat §11) mentadbir hubungan ini.",
          ],
        },
      ],
    },
    {
      heading: "3. Data peribadi apa yang kami proses",
      blocks: [
        { type: "p", text: "Bagi jurugambar/penganjur (kami ialah pengawal):" },
        {
          type: "ul",
          items: [
            "Identiti dan hubungan: nama, e-mel, telefon, nama perniagaan, alamat.",
            "Akaun & keselamatan: kelayakan log masuk (melalui AWS), sesi.",
            "Kewangan: data langganan/pengebilan (melalui Stripe) dan butiran pembayaran keluar (melalui Trolley).",
          ],
        },
        { type: "p", text: "Bagi peserta acara/pembeli (kami ialah pemproses, bagi pihak penganjur):" },
        {
          type: "ul",
          items: [
            "Identiti dan hubungan: nama, e-mel, nombor telefon, alamat pos/pengebilan.",
            "Foto peserta.",
            "Data biometrik — apabila carian muka/selfie diaktifkan (lihat §4).",
            "Data pesanan dan pembayaran (diproses melalui penyedia pembayaran kami; kami tidak menyimpan nombor kad penuh).",
            "Status persetujuan pemasaran, apabila peserta memilih untuk menyertainya.",
          ],
        },
      ],
    },
    {
      heading: "4. Carian muka dan selfie (data biometrik kategori khas)",
      blocks: [
        {
          type: "p",
          text: "Apabila seorang penganjur mengaktifkan carian selfie/muka, Photohawk mencipta perwakilan matematik bagi muka supaya seorang peserta dapat mencari foto mereka sendiri.",
        },
        {
          type: "ul",
          items: [
            "Tujuan: semata-mata untuk memadankan seseorang dengan foto acara mereka sendiri.",
            "Di mana: diproses dan disimpan di Kesatuan Eropah (AWS, wilayah Ireland).",
            "Pengekalan: data muka/biometrik dipadamkan secara automatik apabila galeri tamat tempoh (dalam ~60 hari) dan apabila galeri dipadamkan — yang mana lebih dahulu.",
            "Asas undang-undang (GDPR Art. 9): data biometrik ialah kategori khas dan diberi perlindungan tambahan: ia digunakan hanya supaya orang ramai dapat mencari foto mereka sendiri dalam galeri itu. Sebagai pengawal data bagi peserta, pentadbir galeri memutuskan sama ada carian muka sesuai untuk acaranya dan dibenarkan di tempat acara itu diadakan. Jika undang-undang tempatan memerlukan persetujuan orang yang difoto, pentadbir mesti memperolehnya, contohnya melalui pendaftaran acara; jika tidak, pentadbir patut menggunakan carian nombor bib dan masa sebagai gantinya, yang tidak menggunakan data muka. Photohawk menyediakan langkah persetujuan dalam produk: seorang peserta diminta memberi persetujuan sebelum selfie mereka digunakan untuk carian muka, dan boleh menolak.",
            "Kanak-kanak bawah umur: lihat §13.",
          ],
        },
      ],
    },
    {
      heading: "5. Mengapa kami menggunakan data dan asas undang-undang kami",
      blocks: [
        {
          type: "ul",
          items: [
            "Untuk menyediakan perkhidmatan (mengehos galeri, menjalankan carian, memproses pesanan): pelaksanaan kontrak.",
            "Pembayaran, pembayaran keluar, rekod cukai dan perakaunan: pelaksanaan kontrak dan pematuhan kewajipan undang-undang.",
            "Carian muka/selfie (Art. 9): persetujuan nyata orang yang membuat carian, sebelum selfie mereka digunakan; bagi orang yang difoto, seperti yang diterangkan dalam §4.",
            "E-mel/mesej pemasaran: hanya dengan persetujuan penerima (opt-in), yang boleh ditarik balik pada bila-bila masa.",
            "Kuki analitik dan pengiklanan: hanya dengan persetujuan anda, diberikan melalui sepanduk kuki kami (lihat §12).",
            "Keselamatan, pencegahan penipuan dan penambahbaikan perkhidmatan: kepentingan sah kami, diseimbangkan dengan hak anda.",
          ],
        },
      ],
    },
    {
      heading: "6. Di mana data anda disimpan dan pemindahan antarabangsa",
      blocks: [
        {
          type: "ul",
          items: [
            "Kediaman data: data peserta dan akaun — termasuk foto dan data muka biometrik — dihoskan di Kesatuan Eropah (AWS, Ireland / eu-west-1). Perkhidmatan identiti kami (AWS) juga berada di EU.",
            "Pemindahan antarabangsa: sebahagian daripada sub-pemproses kami (lihat §7) beroperasi di luar EU/UK (cth. di Amerika Syarikat). Apabila data peribadi dipindahkan ke luar EU/UK, kami bergantung pada keputusan kecukupan apabila ia terpakai, dan sebaliknya pada Klausa Kontrak Standard (SCC) Suruhanjaya Eropah bersama-sama Adendum Pemindahan Data Antarabangsa UK, serta perlindungan tambahan apabila diperlukan.",
          ],
        },
      ],
    },
    {
      heading: "7. Sub-pemproses",
      blocks: [
        {
          type: "p",
          text: "Kami menggunakan sebilangan kecil penyedia yang telah disemak untuk menyampaikan perkhidmatan, setiap satu di bawah perjanjian pemprosesan data (dan SCC/Adendum UK apabila mereka memproses data di luar EU/UK):",
        },
        {
          type: "table",
          head: ["Penyedia", "Tujuan", "Data yang terlibat"],
          rows: [
            ["Amazon Web Services (AWS)", "Pengehosan, penyimpanan, identiti, carian muka, e-mel transaksi — wilayah EU", "Semua data peribadi, foto, data biometrik"],
            ["Cloudflare (termasuk R2)", "CDN, domain tersuai, penyimpanan objek", "Foto, data permintaan"],
            ["Stripe", "Pembayaran kad & langganan", "Data pembayaran pembeli & pelanggan"],
            ["Xendit", "Pembayaran (Asia Tenggara)", "Data pembayaran pembeli"],
            ["Paystack", "Pembayaran (Afrika)", "Data pembayaran pembeli"],
            ["Trolley", "Pembayaran keluar kepada jurugambar", "Identiti & butiran bank penerima"],
            ["Postmark", "E-mel transaksi", "Nama, alamat e-mel"],
            ["Twilio / WhatsApp", "Penghantaran foto & pemberitahuan", "Nama, nombor telefon"],
            ["Google (Google Analytics)", "Analitik penggunaan laman web (berasaskan persetujuan)", "Data penggunaan/peranti, pengecam"],
            ["Meta Platforms (Meta Pixel)", "Pengiklanan & pengukuran penukaran (berasaskan persetujuan)", "Data penggunaan, pengecam"],
          ],
        },
        { type: "p", text: "Senarai semasa boleh didapati atas permintaan." },
      ],
    },
    {
      heading: "8. Berapa lama kami menyimpan data (pengekalan)",
      blocks: [
        {
          type: "ul",
          items: [
            "Data muka / biometrik: dipadamkan dalam ~60 hari selepas galeri tamat tempoh, dan apabila galeri dipadamkan (lihat §4).",
            "Foto dan video: pengekalan dikawal oleh pemilik akaun (penganjur/jurugambar). Kandungan disimpan selagi pemilik akaun menyimpannya; pemilik akaun boleh memadamkan foto, video, galeri dan rekod peserta pada bila-bila masa, dan kami memadamkannya apabila mereka berbuat demikian. Photohawk tidak mengenakan jangka hayat tetap ke atas kandungan ini.",
            "Pesanan dan rekod kewangan: disimpan selama 7 tahun untuk memenuhi kewajipan cukai dan perakaunan.",
            "Rekod hubungan peserta/pembeli: dikawal oleh penganjur — disimpan selagi penganjur menyimpannya, dan dipadamkan apabila penganjur memadamkannya atau atas permintaan pemadaman yang disahkan.",
            "Senarai pemasaran: disimpan sehingga persetujuan ditarik balik.",
            "Log operasi/keselamatan disimpan selama 30 hari.",
          ],
        },
      ],
    },
    {
      heading: "9. Cara kami melindungi data",
      blocks: [
        {
          type: "ul",
          items: [
            "Semasa transit: disulitkan melalui HTTPS/TLS di mana-mana.",
            "Semasa rehat: foto dan kandungan disimpan pada AWS dan Cloudflare R2, yang menyulitkan data semasa rehat secara lalai; kelayakan sensitif (cth. token akaun bersambung) turut disulitkan dengan AWS.",
            "Kawalan akses: akses disahkan melalui AWS; akses kakitangan terhad kepada apa yang diperlukan untuk mengendalikan perkhidmatan.",
          ],
        },
      ],
    },
    {
      heading: "10. Hak anda",
      blocks: [
        {
          type: "p",
          text: "Di bawah GDPR/RGPD dan UK GDPR anda mempunyai hak untuk: mengakses data anda; membetulkannya; memadamkannya (“hak untuk dilupakan”); menyekat atau membantah pemprosesan; kemudahalihan data; dan menarik balik persetujuan pada bila-bila masa. Anda juga mempunyai hak untuk membuat aduan kepada pihak berkuasa penyeliaan (ICO di UK, atau pihak berkuasa tempatan anda seperti CNIL di Perancis).",
        },
        { type: "p", text: "Cara melaksanakannya:" },
        {
          type: "ul",
          items: [
            "Jurugambar/penganjur (kami ialah pengawal): hubungi DPO kami di support@photohawk.com. Kami membalas dalam tempoh satu bulan.",
            "Peserta acara/pembeli (penganjur ialah pengawal): sila hubungi jurugambar atau penganjur yang menganjurkan acara anda. Jurugambar boleh memadamkan data peribadi peserta — termasuk foto mereka dan sebarang data carian muka — dari dalam Photohawk, dan Photohawk akan melaksanakan permintaan yang disahkan bagi pihak penganjur.",
          ],
        },
        {
          type: "p",
          text: "Menutup akaun Photohawk membatalkan pengebilan tetapi tidak dengan sendirinya memadamkan data yang disimpan; minta kami memadamkan data jika itu yang anda perlukan.",
        },
      ],
    },
    {
      heading: "11. Perjanjian Pemprosesan Data (DPA)",
      blocks: [
        {
          type: "p",
          text: "Bagi jurugambar/penganjur, pemprosesan data peserta anda oleh kami ditadbir oleh Perjanjian Pemprosesan Data di bawah GDPR Art. 28, yang kami sediakan atas permintaan di support@photohawk.com.",
        },
      ],
    },
    {
      heading: "12. Kuki dan teknologi serupa",
      blocks: [
        {
          type: "ul",
          items: [
            "Kuki yang benar-benar perlu (untuk log masuk, keselamatan dan fungsi teras) sentiasa aktif; ia tidak memerlukan persetujuan.",
            "Kuki analitik dan pengiklanan — Google Analytics dan Meta Pixel (lihat §7) — bukan penting dan hanya dimuatkan selepas anda memilih untuk menyertai melalui sepanduk persetujuan kuki kami. Anda boleh menukar atau menarik balik pilihan anda pada bila-bila masa melalui sepanduk / tetapan kuki. Menolaknya membolehkan perkhidmatan tetap boleh digunakan sepenuhnya.",
            "Butiran lanjut dinyatakan dalam Dasar Kuki kami yang berasingan.",
          ],
        },
      ],
    },
    {
      heading: "13. Data kanak-kanak",
      blocks: [
        {
          type: "p",
          text: "Acara mungkin melibatkan kanak-kanak bawah umur. Sama ada carian muka sesuai untuk sesuatu acara diputuskan oleh pentadbir galeri (lihat §4); apabila kanak-kanak bawah umur mungkin hadir dan undang-undang tempatan memerlukan persetujuan orang yang difoto, pentadbir mesti mempunyai persetujuan ibu bapa atau penjaga bagi mereka, atau menggunakan carian nombor bib dan masa sebagai gantinya. Photohawk tidak dengan sengaja mencipta data biometrik bagi seorang kanak-kanak tanpa persetujuan yang dikehendaki oleh undang-undang, dan langkah persetujuan dalam produk mengukuhkan perkara ini.",
        },
      ],
    },
    {
      heading: "14. Pelanggaran data",
      blocks: [
        {
          type: "p",
          text: "Jika berlaku pelanggaran data peribadi, Photohawk akan memberitahu pengawal data yang terjejas tanpa kelewatan yang tidak wajar selepas menyedarinya, dan akan membantu mereka memenuhi kewajipan pemberitahuan mereka sendiri kepada pihak berkuasa penyeliaan dan individu (GDPR Arts. 33–34).",
        },
      ],
    },
    {
      heading: "15. Perubahan pada dasar ini",
      blocks: [
        {
          type: "p",
          text: "Kami mungkin mengemas kini dasar ini; “Tarikh berkuat kuasa” di atas menunjukkan versi semasa. Perubahan penting akan dimaklumkan kepada pemegang akaun.",
        },
      ],
    },
    {
      heading: "16. Hubungi",
      blocks: [
        {
          type: "p",
          text: "Pegawai Perlindungan Data — Andrew Hammond. CLOUDCONCUR LTD (no. syarikat 13399913), beroperasi dengan nama Photohawk. E-mel: support@photohawk.com · 23 Caldey Gardens, Ingleby Barwick, Stockton-on-Tees, TS17 5HW, United Kingdom.",
        },
      ],
    },
  ],
};

export default gdprMs;
